import { keyBy } from 'lodash';

export const UA_GOVERNANCE = 'ua-governance';

// Native runs against a separate governance interface (`ua-governance-native`)
// so the templates can diverge from the web flow without churn on either
// side. Native bootstrap, refresh, and route resolution use this id;
// `NoCodeTemplates` entries below stay on `ua-governance` so web is
// unaffected. The native nav layer remaps `ua-governance` →
// `ua-governance-native` at lookup time.
export const UA_GOVERNANCE_NATIVE = 'ua-governance-native';

export const NoCodeTemplates: Record<
  | 'login'
  | 'publicLogin'
  | 'updatePassword'
  | 'userActivity'
  | 'mfaVerification'
  | 'forgotPassword'
  | 'resetPassword'
  | 'signup'
  | 'userActivityMatrix',
  { interfaceId: string; interfacePageId: string; interfacePageSlug: string }
> = {
  login: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'login',
    interfacePageId: 'login',
  },
  signup: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'signup',
    interfacePageId: 'signup',
  },
  publicLogin: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'public-login',
    interfacePageId: 'public_login',
  },
  updatePassword: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'update-password',
    interfacePageId: 'update_password',
  },
  mfaVerification: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'mfa-verification',
    interfacePageId: 'mfa_verification',
  },
  userActivity: {
    interfaceId: 'e_6a7087d33d1d5a60606f6078',
    interfacePageSlug: 'ua-settings-user-activity',
    interfacePageId: 'e_6a7087d33d1d5a60606f607c',
  },
  forgotPassword: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'forgot-password',
    interfacePageId: 'ua_governance_forgot_password',
  },
  resetPassword: {
    interfaceId: 'ua-governance',
    interfacePageSlug: 'reset-password',
    interfacePageId: 'ua_governance_reset_password',
  },
  userActivityMatrix: {
    interfaceId: 'e_6a708bb73d1d5a60606f6f97',
    interfacePageSlug: 'ua-settings-user-activity-matrix',
    interfacePageId: 'e_6a708bb73d1d5a60606f6f9b',
  },
};

export const StandardPublicPageSlugs = [
  NoCodeTemplates.login.interfacePageSlug,
  NoCodeTemplates.signup.interfacePageSlug,
  NoCodeTemplates.mfaVerification.interfacePageSlug,
  // do not add update password, it should not be a public page
  // NoCodeTemplates.updatePassword.interfacePageSlug,
  NoCodeTemplates.resetPassword.interfacePageSlug,
  NoCodeTemplates.forgotPassword.interfacePageSlug,
];

// Page IDs in `ua-governance-native` follow a fixed
// `<interface>_<slug-snake>` convention so the renderer can hardcode them
// without hitting a name-resolution table. The slug stays the canonical
// route key — we only swap the pageId at template-resolution time.
export const UA_GOVERNANCE_NATIVE_PAGE_IDS: Record<string, string> = {
  [NoCodeTemplates.login.interfacePageSlug]: 'ua-governance-native_login',
  [NoCodeTemplates.signup.interfacePageSlug]: 'ua-governance-native_signup',
  [NoCodeTemplates.resetPassword.interfacePageSlug]: 'ua-governance-native_reset_password',
  [NoCodeTemplates.forgotPassword.interfacePageSlug]: 'ua-governance-native_forgot_password',
  [NoCodeTemplates.mfaVerification.interfacePageSlug]: 'ua-governance-native_mfa_verification',
  [NoCodeTemplates.updatePassword.interfacePageSlug]: 'ua-governance-native_update_password',
};

// Native registers update-password as a route too — without WebView there's
// no other surface for the post-MFA flow to land on. Web keeps the
// existing `StandardPublicPageSlugs` (excludes update-password by design,
// since the web update-password route is private).
export const NativeAuthSlugs = [
  ...StandardPublicPageSlugs,
  NoCodeTemplates.updatePassword.interfacePageSlug,
];

const ExcludeNavigationSlugs = [
  ...StandardPublicPageSlugs,
  NoCodeTemplates.updatePassword.interfacePageSlug,
  NoCodeTemplates.publicLogin.interfacePageSlug,
];

export function enableNavigationBySlug(slug: string | undefined): boolean {
  if (!slug) return true;
  return !ExcludeNavigationSlugs.includes(slug);
}

export const NO_CODE_TEMPLATES_BY_ID = keyBy(
  Object.values(NoCodeTemplates),
  'interfacePageId',
) as Record<string, { interfaceId: string; interfacePageSlug: string } | undefined>;
